/**
 * Criação de etiqueta a partir da tela de atendimento.
 *
 * Vai pelo Chatwoot, não direto no Aceleres: quem valida e cria a Label é o
 * Rails, que só pede a cor ao funil por trás. O caminho antigo — o navegador
 * chamando `homolog.aceleres.ai` com o cookie do Aceleres — exigiria a pessoa
 * logada nos dois sistemas no mesmo navegador.
 */
import Auth from '../auth';

const accountId = () => {
  if (window.location.pathname.includes('/app/accounts')) {
    return window.location.pathname.split('/')[3];
  }

  return '';
};

/**
 * Um `fetch` cru não passa pelo `wootApi` do APIHelper, então os cabeçalhos do
 * devise_token_auth precisam vir na mão — sem eles o `authenticate_user!`
 * derruba a requisição antes do controller e devolve 401.
 */
const authHeaders = () => {
  const authData = Auth.getAuthData();

  if (!authData) return {};

  const {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  } = authData;

  return {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  };
};

/**
 * Prévia do nome que a etiqueta vai receber.
 *
 * Precisa casar com o `ActiveSupport::Inflector.transliterate` do controller e
 * com o `Str::slug` do funil: acento vira a letra sem acento, e não sumiço.
 * O `sanitizeLabel` que o resto da tela usa faz o contrário — por ele
 * "Negociação" viraria "negociao", e a pessoa veria um nome que não é o que
 * ela vai receber.
 *
 * @param {string} title
 * @returns {string}
 */
export const funilLabelTitle = (title = '') =>
  String(title)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Cria a etiqueta e devolve a label pronta do Chatwoot.
 *
 * @param {string} title Nome digitado por quem atende, sem tratamento.
 * @param {string} color Cor escolhida para a etiqueta.
 * @returns {Promise<{payload: Object, etapa: null}>}
 */
export const createFunilLabel = async (title, color) => {
  const response = await fetch(
    `/api/v1/accounts/${accountId()}/aceleres/funil_labels`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify({ title, color }),
    }
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.success === false) {
    throw new Error(data.message || 'Não foi possível criar a etiqueta.');
  }

  return { payload: data.payload, etapa: data.etapa };
};

/**
 * Titulos que ESTA funcionalidade criou nesta conta.
 *
 * E o que decide em quais etiquetas a tela mostra o botao de apagar: etapa do
 * funil, marcadores do atendimento e etiquetas feitas em Configuracoes ficam
 * de fora de proposito.
 *
 * @returns {Promise<string[]>}
 */
export const fetchManagedFunilLabels = async () => {
  const response = await fetch(
    `/api/v1/accounts/${accountId()}/aceleres/funil_labels`,
    {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...authHeaders() },
    }
  );
  const data = await response.json().catch(() => ({}));
  return Array.isArray(data.titles) ? data.titles : [];
};

/**
 * Em quantas conversas a etiqueta esta. Vai na confirmacao, porque apagar
 * remove de todas — nao so da conversa aberta.
 *
 * @param {number} id
 * @returns {Promise<number>}
 */
export const fetchFunilLabelUsage = async id => {
  const response = await fetch(
    `/api/v1/accounts/${accountId()}/aceleres/funil_labels/${id}/usage`,
    {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...authHeaders() },
    }
  );
  const data = await response.json().catch(() => ({}));
  return Number(data.conversations || 0);
};

/**
 * Apaga a etiqueta: tira das conversas e destroi o registo.
 *
 * @param {number} id
 * @returns {Promise<number>} conversas de que a etiqueta foi removida
 */
export const deleteFunilLabel = async id => {
  const response = await fetch(
    `/api/v1/accounts/${accountId()}/aceleres/funil_labels/${id}`,
    {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...authHeaders() },
    }
  );
  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.success === false) {
    throw new Error(data.message || 'Não foi possível apagar a etiqueta.');
  }

  return Number(data.conversations || 0);
};

export default {
  createFunilLabel,
  funilLabelTitle,
  fetchManagedFunilLabels,
  fetchFunilLabelUsage,
  deleteFunilLabel,
};
